import { useCallback, useEffect, useRef } from 'react';
import { capture } from '../lib/analytics';
import { createDwellTracker } from '../lib/pageDwellTracker';

/**
 * Emits `billing_page_exited` when the reader leaves /billing.
 *
 * WHY THE PAGE NEEDED THIS AT ALL. /billing emitted two MOUNT events
 * (`billing_page_viewed`, `billing_page_data_loaded`) and nothing else, and
 * there is no ambient click stream to fall back on: the dashboard drops every
 * `$autocapture` in `before_send` (lib/posthogConfig.ts
 * `dropRawAutocaptureEvents`, shipped ca2503bed 2026-07-14; measured 0 rows
 * for `$host = app.overlayqa.com` over 90 days against 10,850 for
 * overlayqa.com, which runs its own config). So the page could not report how
 * long anyone stayed, whether the prices ever reached the screen, or whether
 * the period toggle was touched -- the three signals that separate a
 * considered rejection from a two-second glance. A CTA click was already
 * covered (`trial_expired_grid_cta_clicked`, `billing_pricing_table_click`);
 * this covers everyone who never clicked one, which is the population a
 * pricing read is about.
 *
 * EXIT IS AN UNMOUNT, NOT A `pagehide`. Inside the dashboard SPA the normal
 * way to leave /billing is a react-router route change, which fires no
 * `pagehide` and no `beforeunload`. The 2026-09-11 visit this was written for
 * had to be timed by hand off an unrelated `trial_nudge_shown` that happened
 * to carry `$pathname: /dashboard` -- 2.47s, where a board row reading only
 * named product events had reported 28.5s. So the primary trigger is the
 * effect cleanup, with `pagehide` as the tab-close path.
 *
 * DELIBERATELY NOT FIRED ON `visibilitychange`. The report's inline version is
 * one-shot on first hidden because a static report has no unmount to hook.
 * Doing that here would end the visit when someone flicks to another tab to
 * check a price against a competitor and comes back -- exactly the considered
 * reader we most want to measure. Hiding pauses the active clock instead.
 */
const TICK_MS = 1000;

/**
 * A mount shorter than this, with nothing reached and nothing touched, is not a
 * visit and must not be reported as one.
 *
 * READ OFF A RUN, not chosen by feel. React.StrictMode (main.tsx:71)
 * double-invokes effects mount -> unmount -> mount SYNCHRONOUSLY, and in the
 * 2026-09-13 browser A/B that synthetic unmount emitted a real-looking exit
 * with `total_ms: 2, surface: none` -- which, landing FIRST, shadowed the
 * genuine exit for every consumer that reads the first row. 250ms sits two
 * orders of magnitude above that 2ms and far below any human visit.
 *
 * The test is CONJUNCTIVE on purpose, so it cannot swallow the population this
 * event exists for: a reader who bounces in 80ms having reached the plan grid,
 * or touched the toggle, still reports. Only a mount that lasted no time, drew
 * nothing and was touched by nobody is dropped.
 */
const MIN_REAL_VISIT_MS = 250;

export type BillingInteraction = 'toggle' | 'cta';

/** Where the measurement came from when the reader left. */
type ExitReason = 'route_change' | 'pagehide';

export interface BillingPageExit {
  /**
   * Attach to the element wrapping the plan chooser. Drives
   * `plan_grid_reached`, which is `null` when no chooser ever mounted or the
   * browser has no IntersectionObserver -- never silently `false`, because
   * "we could not tell" and "they never got there" are different findings.
   */
  chooserRef: (node: HTMLElement | null) => void;
  /** Record a decision gesture so the exit event can carry it. */
  markInteraction: (kind: BillingInteraction) => void;
}

export function useBillingPageExit({
  resolveProps,
}: {
  /** Read at EXIT time, so plan data that arrives after mount still lands. */
  resolveProps: () => Record<string, unknown>;
}): BillingPageExit {
  const resolvePropsRef = useRef(resolveProps);
  resolvePropsRef.current = resolveProps;

  const interactionsRef = useRef({ toggle: false, cta: false });
  const reachedRef = useRef<boolean | null>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);

  const markInteraction = useCallback((kind: BillingInteraction) => {
    interactionsRef.current[kind] = true;
  }, []);

  const chooserRef = useCallback((node: HTMLElement | null) => {
    observerRef.current?.disconnect();
    observerRef.current = null;
    if (!node) return;
    if (typeof IntersectionObserver === 'undefined') {
      reachedRef.current = null;
      return;
    }
    // Mounted and observable, so "not reached yet" is now a real answer.
    reachedRef.current = reachedRef.current === true ? true : false;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          reachedRef.current = true;
          observer.disconnect();
        }
      },
      { threshold: 0.25 },
    );
    observer.observe(node);
    observerRef.current = observer;
  }, []);

  useEffect(() => {
    const tracker = createDwellTracker({ now: () => Date.now(), tickMs: TICK_MS });
    // Per-effect-run, NOT a useRef. React.StrictMode (on in main.tsx:71)
    // double-invokes effects mount -> unmount -> mount on the same instance,
    // and a ref survives that synthetic unmount -- so a latch held in a ref is
    // already spent before the reader ever leaves, and the real exit is
    // silently dropped. Pinned by the STRICTMODE CONTROL in the sibling test.
    let sent = false;

    const readScrollPct = (): number => {
      const doc = document.documentElement;
      const denom = doc.scrollHeight - doc.clientHeight;
      // A page short enough not to scroll was seen in full; that is 100, not 0.
      if (denom <= 0) return 100;
      return ((doc.scrollTop || 0) / denom) * 100;
    };

    const emit = (exitReason: ExitReason) => {
      if (sent) return;
      // Read the depth HERE, not at mount. Seeding at mount sampled an empty
      // page, where scrollHeight === clientHeight reads as "saw it all" (100),
      // and since max_scroll_pct only climbs that latched before the plan grid
      // had rendered -- the browser A/B reported 100 on a page it never
      // scrolled. At exit the page is whatever the reader actually had.
      tracker.noteScroll(readScrollPct());
      const snapshot = tracker.snapshot();
      const touched = interactionsRef.current.toggle || interactionsRef.current.cta;
      if (!touched && reachedRef.current !== true && snapshot.total_ms < MIN_REAL_VISIT_MS) {
        // Deliberately does NOT latch: a later, real exit in this same effect
        // run must still be free to report.
        return;
      }
      sent = true;
      capture('billing_page_exited', {
        ...resolvePropsRef.current(),
        ...snapshot,
        plan_grid_reached: reachedRef.current,
        period_toggled: interactionsRef.current.toggle,
        cta_clicked: interactionsRef.current.cta,
        exit_reason: exitReason,
      });
    };

    const onInput = () => tracker.noteInput();
    const onScroll = () => {
      tracker.noteInput();
      tracker.noteScroll(readScrollPct());
    };
    const onPageHide = () => emit('pagehide');

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('pointerdown', onInput, { passive: true });
    window.addEventListener('keydown', onInput, { passive: true });
    window.addEventListener('pagehide', onPageHide);

    const interval = window.setInterval(() => {
      tracker.tick(document.visibilityState !== 'hidden');
    }, TICK_MS);

    return () => {
      window.clearInterval(interval);
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('pointerdown', onInput);
      window.removeEventListener('keydown', onInput);
      window.removeEventListener('pagehide', onPageHide);
      observerRef.current?.disconnect();
      observerRef.current = null;
      emit('route_change');
    };
  }, []);

  return { chooserRef, markInteraction };
}
